import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@workspace/ui/components/dialog'
import { EmptyState } from '@workspace/ui/components/empty-state'
import { FileText } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { ImageViewer } from './image-viewer'
import { PdfViewer } from './pdf-viewer'

export interface PreviewDocument {
  id: string
  fileName: string
  mimeType: string | null
}

export interface DocumentPreviewModalProps {
  document: PreviewDocument | null
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Fetch presigned download URL for a document. */
  fetchUrl: (documentId: string) => Promise<string>
  /** Called when the modal opens (for tracking). */
  onOpen?: (documentId: string) => void
  /** Called when the modal closes (for tracking). */
  onClose?: (documentId: string) => void
  /** Called when the PDF page changes (navigation or initial load). */
  onPageChange?: (page: number, totalPages: number) => void
}

function getViewerType(mimeType: string | null): 'pdf' | 'image' | 'unsupported' {
  if (!mimeType) return 'unsupported'
  if (mimeType === 'application/pdf') return 'pdf'
  if (mimeType.startsWith('image/')) return 'image'
  return 'unsupported'
}

export function DocumentPreviewModal({
  document,
  open,
  onOpenChange,
  fetchUrl,
  onOpen,
  onClose,
  onPageChange,
}: DocumentPreviewModalProps) {
  const [documentUrl, setDocumentUrl] = useState<string | null>(null)
  const [isPending, setIsPending] = useState(false)
  const [isError, setIsError] = useState(false)

  const onOpenRef = useRef(onOpen)
  onOpenRef.current = onOpen
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose
  const documentIdRef = useRef(document?.id)

  const fetchUrlRef = useRef(fetchUrl)
  fetchUrlRef.current = fetchUrl

  useEffect(() => {
    if (!open || !document?.id) return
    documentIdRef.current = document.id
    setDocumentUrl(null)
    setIsError(false)
    setIsPending(true)

    onOpenRef.current?.(document.id)

    fetchUrlRef
      .current(document.id)
      .then(setDocumentUrl)
      .catch(() => setIsError(true))
      .finally(() => setIsPending(false))
  }, [open, document?.id])

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen && documentIdRef.current) {
        onCloseRef.current?.(documentIdRef.current)
      }
      onOpenChange(nextOpen)
    },
    [onOpenChange]
  )

  const viewerType = getViewerType(document?.mimeType ?? null)

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="flex h-[90vh] max-w-7xl flex-col gap-0 p-0 sm:max-w-7xl">
        <DialogHeader className="border-b border-border px-6 py-4">
          <DialogTitle className="truncate pr-8">{document?.fileName}</DialogTitle>
        </DialogHeader>

        <div className="flex min-h-0 flex-1 items-center justify-center overflow-hidden">
          {isPending && (
            <EmptyState
              icon={<FileText className="size-12 animate-pulse" />}
              title="Loading document..."
              dashed={false}
              className="border-none"
            />
          )}

          {isError && (
            <EmptyState
              icon={<FileText className="size-12" />}
              title="Failed to load document"
              dashed={false}
              className="border-none text-red-400"
            />
          )}

          {documentUrl && viewerType === 'pdf' && (
            <PdfViewer url={documentUrl} className="h-full w-full" onPageChange={onPageChange} />
          )}

          {documentUrl && viewerType === 'image' && (
            <ImageViewer url={documentUrl} alt={document?.fileName} className="h-full" />
          )}

          {documentUrl && viewerType === 'unsupported' && (
            <EmptyState
              icon={<FileText className="size-12" />}
              title="Preview not available for this file type"
              dashed={false}
              className="border-none"
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
